import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { IconBook } from "@tabler/icons-react";
import { AnimatePresence, motion } from "framer-motion";
import { LuX } from "react-icons/lu";
import { File as CustomFile } from "@/types";
import { SetStateAction, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { v4 as uuidv4 } from "uuid";
import { withCacheBuster } from "@/components/common/adCard/Media";

const isVideoFile = (fileName: string | undefined) => {
  if (!fileName) return false;
  return /(\.mp4$|\.webm$)/i.test(fileName);
};

const LibraryAttachmentsPreview = ({
  attachments,
  className = "",
  onRemove,
}: {
  attachments: CustomFile[];
  className?: string;
  onRemove?: (media: CustomFile) => void;
}) => {
  const [preview, setPreview] = useState<{ file: CustomFile; layoutId: string } | null>(
    null,
  );
  const [layoutTrigger, setLayoutTrigger] = useState(0);

  return (
    <>
      <div
        className={cn(
          `flex flex-nowrap items-center gap-2 overflow-y-visible scrollbar-hide py-2`,
          className,
        )}
      >
        {attachments.map((attachment, index) => (
          <MediaThumbnail
            key={`${attachment.id}-${index}`}
            attachment={attachment}
            onRemove={onRemove}
            setPreview={(preview) => {
              setLayoutTrigger(Date.now());
              setPreview(preview);
            }}
            layoutTrigger={layoutTrigger}
          />
        ))}
      </div>

      {/* Fullscreen Preview - Rendered via Portal to document.body */}
      {createPortal(
        <AnimatePresence>
          {preview && (
            <>
              {/* Overlay */}
              <motion.div
                key="overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={() => {
                  setLayoutTrigger(Date.now());
                  setPreview(null);
                }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
              />

              {/* Enlarged Preview */}
              <motion.div
                key="previewWrapper"
                className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none"
              >
                {isVideoFile(preview.file.file_name) ? (
                  <motion.video
                    src={preview.file.public_url}
                    layoutId={preview.layoutId}
                    className="max-h-[80vh] max-w-[80vw] object-contain rounded-md pointer-events-auto"
                    controls
                  />
                ) : (
                  <motion.img
                    crossOrigin="anonymous"
                    src={withCacheBuster(preview.file.public_url)}
                    layoutId={preview.layoutId}
                    className="max-h-[80vh] max-w-[80vw] object-contain rounded-md pointer-events-auto"
                  />
                )}
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
};

const MediaThumbnail = ({
  setPreview,
  attachment,
  onRemove,
  layoutTrigger,
}: {
  attachment: CustomFile;
  setPreview: React.Dispatch<
    SetStateAction<{ file: CustomFile; layoutId: string } | null>
  >;
  onRemove?: (file: CustomFile) => void;
  layoutTrigger: number;
}) => {
  const layoutId = useMemo(() => uuidv4(), []);
  return (
    <div key={layoutId} className="relative group flex-none">
      {/* Diagonal shadow overlay in top-left corner */}
      <div
        className="absolute h-full w-full rounded-[8px] pointer-events-none"
        style={{
          background:
            "linear-gradient(313.48deg, rgba(0, 0, 0, 0) 42.34%, rgba(0, 0, 0, 0.4) 100%)",
        }}
      ></div>

      {/* Preview Image / Video */}
      {isVideoFile(attachment.file_name) ? (
        <motion.video
          src={attachment.public_url}
          layoutId={layoutId}
          layoutDependency={layoutTrigger}
          className="h-[60px] w-[60px] rounded-[8px] border-[1px] border-[#E7E9EF] object-cover cursor-pointer flex-shrink-0"
          style={{ aspectRatio: "1/1" }}
          muted
          loop
          playsInline
          onClick={() => setPreview({ file: attachment, layoutId })}
        />
      ) : (
        <motion.img
          crossOrigin="anonymous"
          src={withCacheBuster(attachment.public_url)}
          layoutId={layoutId}
          layoutDependency={layoutTrigger}
          className="h-[60px] w-[60px] rounded-[8px] border-[1px] border-[#E7E9EF] object-cover cursor-pointer flex-shrink-0"
          style={{ aspectRatio: "1/1" }}
          onClick={() => setPreview({ file: attachment, layoutId })}
        />
      )}
      {/* Remove Button (shown on hover or always if desired) */}
      {onRemove && (
        <button
          type="button"
          onClick={() => onRemove(attachment)}
          className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-[#09090B80] shadow-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <LuX size={12} />
        </button>
      )}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <IconBook
              size={16}
              color="#fff"
              className="absolute top-[6px] left-[8px] z-10"
            />
          </TooltipTrigger>
          <TooltipContent align="start" collisionPadding={10} side="top">
            Library
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export default LibraryAttachmentsPreview;
